// @px7digital/system-libraries/auth-overlay
//
// <PX7AuthOverlay> — one styled, in-app sign-in / create-account overlay
// that every PX7 app can drop in instead of bouncing the user out to the
// accounts.px7 page. It owns the UI, validation, and loading/error/notice
// states; it knows nothing about the network. Where the credentials go is
// the `transport`'s job (see ./transports.js), so the same overlay serves
// both localStorage-JWT SPAs and cookie-session BFF apps.
//
// Authored in plain JS (React.createElement, no JSX) so the shared library
// stays build-step-free and consumable as-is by any app's bundler. React
// is a peer dependency — only apps that import this module pull it in.
//
// Theming: every visual value is a `--px7-auth-*` CSS variable (see
// ./styles.js). Pass the `theme` prop to set them inline on the root, or
// override them in your own stylesheet under `.px7-auth`. The result reads
// as native to your app while sharing one auth flow.
//
// Minimal usage (GoDating, BFF):
//   import { PX7AuthOverlay, createBackendTransport }
//     from "@px7digital/system-libraries/auth-overlay";
//   <PX7AuthOverlay
//     appName="GoDating"
//     transport={createBackendTransport({
//       loginUrl: "/api/auth/px7/login",
//       signupUrl: "/api/auth/px7/signup",
//     })}
//     theme={{ accent: "#e0623f", accentInk: "#fff", radius: "22px" }}
//     onAuthenticated={() => location.assign("/")}
//   />

import {
  createElement as h,
  Fragment,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { ensureStyles } from "./styles.js";

export {
  createAccountsTransport,
  createBackendTransport,
  AuthRequestError,
} from "./transports.js";

const THEME_VARS = {
  font: "--px7-auth-font",
  backdrop: "--px7-auth-backdrop",
  surface: "--px7-auth-surface",
  ink: "--px7-auth-ink",
  muted: "--px7-auth-muted",
  border: "--px7-auth-border",
  fieldBg: "--px7-auth-field-bg",
  fieldBorder: "--px7-auth-field-border",
  fieldRadius: "--px7-auth-field-radius",
  accent: "--px7-auth-accent",
  accentInk: "--px7-auth-accent-ink",
  accentHover: "--px7-auth-accent-hover",
  radius: "--px7-auth-radius",
  danger: "--px7-auth-danger",
  dangerBg: "--px7-auth-danger-bg",
  noticeBg: "--px7-auth-notice-bg",
  shadow: "--px7-auth-shadow",
  maxWidth: "--px7-auth-max-width",
};

function themeToVars(theme) {
  if (!theme) return {};
  const vars = {};
  for (const [key, cssVar] of Object.entries(THEME_VARS)) {
    if (theme[key] != null) vars[cssVar] = theme[key];
  }
  return vars;
}

const DEFAULT_COPY = {
  login: {
    title: "Welcome back",
    subtitle: "Sign in to continue.",
    submit: "Sign in",
    submitting: "Signing in…",
  },
  create: {
    title: "Create your account",
    subtitle: "One account works across all our apps.",
    submit: "Create account",
    submitting: "Creating account…",
  },
  reset: {
    title: "Reset your password",
    subtitle: "We'll email you a link to set a new password.",
    submit: "Send reset link",
    submitting: "Sending…",
    sent: "If an account exists for that email, a reset link is on its way.",
  },
};

function renderLogo(logo) {
  if (!logo) return null;
  const inner =
    typeof logo === "string"
      ? h("img", { src: logo, alt: "", "aria-hidden": "true" })
      : logo;
  return h("div", { className: "px7-auth__logo" }, inner);
}

/**
 * @param {object} props
 * @param {object} props.transport            Required. login/signup/reset.
 * @param {string} [props.appName]            Brand name used in default copy.
 * @param {boolean} [props.open=true]         Controlled visibility.
 * @param {boolean} [props.inline=false]      Render inline (no backdrop/dialog).
 * @param {boolean} [props.dismissible=true]  Allow ESC / backdrop / close button.
 * @param {() => void} [props.onClose]
 * @param {(result:any, ctx:{mode:string}) => void} [props.onAuthenticated]
 * @param {"login"|"create"} [props.initialMode="login"]
 * @param {boolean} [props.allowCreate=true]  Show the create-account tab.
 * @param {React.ReactNode|string} [props.logo]
 * @param {object} [props.copy]               Per-mode copy overrides.
 * @param {string} [props.ssoFallbackHref]    Show a "classic sign-in" link.
 * @param {string} [props.ssoFallbackLabel="Use classic sign-in"]
 * @param {React.ReactNode} [props.footer]
 * @param {object} [props.theme]              Maps to --px7-auth-* vars.
 * @param {number} [props.minPasswordLength=8]
 * @param {string} [props.className]
 */
export function PX7AuthOverlay(props) {
  const {
    transport,
    appName,
    open = true,
    inline = false,
    dismissible = true,
    onClose,
    onAuthenticated,
    initialMode = "login",
    allowCreate = true,
    logo,
    copy: copyOverride,
    ssoFallbackHref,
    ssoFallbackLabel = "Use classic sign-in",
    footer,
    theme,
    minPasswordLength = 8,
    className,
  } = props;

  const [mode, setMode] = useState(initialMode === "create" ? "create" : "login");
  const [resetting, setResetting] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const emailRef = useRef(null);

  useEffect(() => {
    ensureStyles();
  }, []);

  const canDismiss = dismissible && !inline && typeof onClose === "function";

  const close = useCallback(() => {
    if (canDismiss) onClose();
  }, [canDismiss, onClose]);

  // ESC to dismiss.
  useEffect(() => {
    if (!open || !canDismiss) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") close();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, canDismiss, close]);

  // Focus the email field when the panel appears or the mode flips.
  useEffect(() => {
    if (!open) return;
    const id = setTimeout(() => {
      if (emailRef.current) emailRef.current.focus();
    }, 60);
    return () => clearTimeout(id);
  }, [open, mode, resetting]);

  const switchMode = (next) => {
    setMode(next);
    setResetting(false);
    setError("");
    setNotice("");
  };

  const startReset = () => {
    setResetting(true);
    setError("");
    setNotice("");
  };

  const view = resetting ? "reset" : mode;
  const copy = { ...DEFAULT_COPY[view], ...((copyOverride && copyOverride[view]) || {}) };

  const defaultTitle =
    view === "login" && appName ? `Sign in to ${appName}` : copy.title;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setError("");
    setNotice("");

    const trimmedEmail = email.trim();
    if (!trimmedEmail) {
      setError("Enter your email.");
      return;
    }

    if (view === "reset") {
      if (typeof transport.reset !== "function") {
        setError("Password reset isn't available here.");
        return;
      }
      setSubmitting(true);
      try {
        await transport.reset({ email: trimmedEmail });
        setNotice(copy.sent);
      } catch (err) {
        setError(messageOf(err));
      } finally {
        setSubmitting(false);
      }
      return;
    }

    if (!password) {
      setError("Enter your password.");
      return;
    }
    if (view === "create" && password.length < minPasswordLength) {
      setError(`Use a password of at least ${minPasswordLength} characters.`);
      return;
    }

    setSubmitting(true);
    try {
      const fn = view === "create" ? transport.signup : transport.login;
      const result = await fn({ email: trimmedEmail, password });
      if (typeof onAuthenticated === "function") {
        onAuthenticated(result, { mode: view });
      }
    } catch (err) {
      setError(messageOf(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  const rootStyle = themeToVars(theme);
  const rootClass = `px7-auth${inline ? " px7-auth--inline" : ""}${className ? ` ${className}` : ""}`;

  const card = h(
    "div",
    {
      className: "px7-auth__card",
      role: inline ? undefined : "dialog",
      "aria-modal": inline ? undefined : "true",
      "aria-label": defaultTitle,
      onClick: (e) => e.stopPropagation(),
    },
    canDismiss
      ? h(
          "button",
          { type: "button", className: "px7-auth__close", "aria-label": "Close", onClick: close },
          "×",
        )
      : null,

    h(
      "div",
      { className: "px7-auth__head" },
      renderLogo(logo),
      h("h2", { className: "px7-auth__title" }, defaultTitle),
      copy.subtitle ? h("p", { className: "px7-auth__subtitle" }, copy.subtitle) : null,
    ),

    // Segmented login / create switch (hidden in reset view).
    allowCreate && !resetting
      ? h(
          "div",
          { className: "px7-auth__seg", role: "tablist", "aria-label": "Sign in or create account" },
          h(
            "button",
            {
              type: "button",
              role: "tab",
              className: "px7-auth__seg-btn",
              "aria-selected": mode === "login" ? "true" : "false",
              onClick: () => switchMode("login"),
            },
            "Sign in",
          ),
          h(
            "button",
            {
              type: "button",
              role: "tab",
              className: "px7-auth__seg-btn",
              "aria-selected": mode === "create" ? "true" : "false",
              onClick: () => switchMode("create"),
            },
            "Create account",
          ),
        )
      : null,

    h(
      "form",
      { className: "px7-auth__form", onSubmit, noValidate: true },

      error ? h("p", { className: "px7-auth__error", role: "alert" }, error) : null,
      notice ? h("p", { className: "px7-auth__notice", role: "status" }, notice) : null,

      h(
        "div",
        { className: "px7-auth__field" },
        h("label", { className: "px7-auth__label", htmlFor: "px7-auth-email" }, "Email"),
        h("input", {
          id: "px7-auth-email",
          ref: emailRef,
          className: "px7-auth__input",
          type: "email",
          name: "email",
          autoComplete: "email",
          inputMode: "email",
          autoCapitalize: "none",
          spellCheck: false,
          placeholder: "you@example.com",
          value: email,
          "aria-invalid": error && !email.trim() ? "true" : undefined,
          onChange: (e) => setEmail(e.target.value),
          disabled: submitting,
        }),
      ),

      view !== "reset"
        ? h(
            "div",
            { className: "px7-auth__field" },
            h(
              "label",
              { className: "px7-auth__label", htmlFor: "px7-auth-password" },
              "Password",
            ),
            h(
              "div",
              { className: "px7-auth__pw-wrap" },
              h("input", {
                id: "px7-auth-password",
                className: "px7-auth__input",
                type: showPw ? "text" : "password",
                name: "password",
                autoComplete: view === "create" ? "new-password" : "current-password",
                placeholder: view === "create" ? "At least 8 characters" : "Your password",
                value: password,
                onChange: (e) => setPassword(e.target.value),
                disabled: submitting,
              }),
              h(
                "button",
                {
                  type: "button",
                  className: "px7-auth__pw-toggle",
                  "aria-label": showPw ? "Hide password" : "Show password",
                  onClick: () => setShowPw((v) => !v),
                  tabIndex: -1,
                },
                showPw ? "Hide" : "Show",
              ),
            ),
          )
        : null,

      h(
        "button",
        { type: "submit", className: "px7-auth__submit", disabled: submitting },
        submitting ? h("span", { className: "px7-auth__spinner", "aria-hidden": "true" }) : null,
        submitting ? copy.submitting : copy.submit,
      ),

      // Forgot-password / back links.
      view === "login" && typeof transport.reset === "function"
        ? h(
            "div",
            { className: "px7-auth__row" },
            h("button", { type: "button", className: "px7-auth__link", onClick: startReset }, "Forgot password?"),
          )
        : null,
      resetting
        ? h(
            "div",
            { className: "px7-auth__row" },
            h(
              "button",
              { type: "button", className: "px7-auth__link", onClick: () => switchMode("login") },
              "Back to sign in",
            ),
          )
        : null,
    ),

    // Optional SSO fallback (the classic redirect flow).
    ssoFallbackHref && !resetting
      ? h(
          Fragment,
          null,
          h("div", { className: "px7-auth__divider" }, "or"),
          h("a", { className: "px7-auth__sso", href: ssoFallbackHref }, ssoFallbackLabel),
        )
      : null,

    footer ? h("div", { className: "px7-auth__foot" }, footer) : null,
  );

  if (inline) {
    return h("div", { className: rootClass, style: rootStyle }, card);
  }

  return h(
    "div",
    {
      className: rootClass,
      style: rootStyle,
    },
    h(
      "div",
      {
        className: "px7-auth__backdrop",
        onClick: canDismiss ? close : undefined,
      },
      card,
    ),
  );
}

function messageOf(err) {
  if (err && typeof err.message === "string" && err.message) return err.message;
  return "Something went wrong. Try again.";
}

export default PX7AuthOverlay;
